import React, { useState, useEffect } from 'react'

import { LANGUAGES } from 'constants/locale'
import { useCompany, useUser } from 'contexts'
import { Locale } from 'enums/locale'

type LocaleContextType = {
  locale: Locale | string
  setLocale: (value: Locale) => void
  APILanguageChanged: boolean
  setAPILanguageChanged: (value: boolean) => void
}
export const LocaleContext = React.createContext<LocaleContextType | undefined>(
  undefined,
)

type Props = {
  children: React.ReactNode
}

export const LocaleProvider = ({ children }: Props) => {
  const { company } = useCompany()!
  const { user } = useUser()!

  const [locale, setLocale] = useState<any>(Locale.English)
  const [APILanguageChanged, setAPILanguageChanged] = useState(false)

  const getLocale = () => {
    if (user?.preferred_language) {
      const language: any = LANGUAGES.find(
        language => language.id === user?.preferred_language,
      )

      if (language.value) {
        localStorage.setItem('locale', language.value)
        return language.value
      }
    }

    // Read from localStorage fresh each time, not cached
    return localStorage.getItem('locale')
  }

  useEffect(() => {
    // Always hide Weglot's default widget — the custom LanguageSwitcher in the
    // topbar handles language switching instead.
    let style = document.getElementById('weglot-visibility-style')
    if (!style) {
      style = document.createElement('style')
      style.id = 'weglot-visibility-style'
      document.head.appendChild(style)
    }
    style.innerHTML = '.wg-drop { display: none !important; }'

    if (company && company.allow_internationalization) {
      const currentLocale = getLocale()
      setLocale(currentLocale || Locale.English)
    }

    if (company && !company.allow_internationalization) {
      setLocale(Locale.English)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, user])

  return (
    <LocaleContext.Provider
      value={{ locale, setLocale, APILanguageChanged, setAPILanguageChanged }}
    >
      {children}
    </LocaleContext.Provider>
  )
}

export const useLocale = () => React.useContext(LocaleContext)
